// src/hooks/useRoleAccess.js - Role-based access control hook

import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../services/api';
import { hasAccess, getRedirectPath } from '../config/roleConfig';

/**
 * Hook to enforce role-based access control
 * Automatically redirects users if they try to access unauthorized pages
 *
 * @param {boolean} enabled - Whether to enforce access control (default: true)
 */
export const useRoleAccess = (enabled = true) => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!enabled) return;

    const currentUser = authService.getCurrentUser();

    if (!currentUser || !currentUser.email) {
      // Not authenticated, let PrivateRoute handle it
      return;
    }

    const currentPath = location.pathname;
    const userHasAccess = hasAccess(currentUser.email, currentPath);

    if (!userHasAccess) {
      const redirectTo = getRedirectPath(currentUser.email, currentPath);
      console.log(`[Role Access] User ${currentUser.email} denied access to ${currentPath}, redirecting to ${redirectTo}`);
      navigate(redirectTo, { replace: true });
    }
  }, [location.pathname, enabled, navigate]);
};

/**
 * Hook to get user's role-specific information
 *
 * @returns {Object} User role information
 */
export const useUserRole = () => {
  const currentUser = authService.getCurrentUser();

  if (!currentUser || !currentUser.email) {
    return {
      email: null,
      role: null,
      isBQM: false,
      isFinanceManager: false,
      isPlanner: false
    };
  }

  const emailLower = currentUser.email.toLowerCase();
  const isBQM = emailLower === 'bqm@j49.com';
  const isFinanceManager = emailLower === 'financemanager@j49.com';
  const isPlanner = !isBQM && !isFinanceManager;

  return {
    email: currentUser.email,
    role: isBQM ? 'bqm' : isFinanceManager ? 'financeManager' : 'planner',
    isBQM,
    isFinanceManager,
    isPlanner
  };
};
